import { useState } from "react";
import { Link } from "react-router-dom";
import { HelpCircle, Check, Minus, Hash, TrendingUp, Sparkles } from "lucide-react";
import "../landing/landing.css";

// ---------------------------------------------------------------------------
// Content
// ---------------------------------------------------------------------------

// Sample coverage map for an example site. Values read as samples, not claims.
const SAMPLE_TOPIC = "coworking space jakarta";
const SAMPLE_SITE = "example.com";

// Sample Search Console queries for the hero switch: the same list, sorted two ways.
const QUERIES = [
  { query: "what is coworking", stage: "Unaware", clicks: 1200, fit: 0.1 },
  { query: "how to focus working from home", stage: "Problem-aware", clicks: 900, fit: 0.2 },
  { query: "coworking meaning", stage: "Unaware", clicks: 700, fit: 0.1 },
  { query: "best coworking space jakarta", stage: "Solution-aware", clicks: 210, fit: 0.8 },
  { query: "example space pricing", stage: "Product-aware", clicks: 90, fit: 0.9 },
  { query: "book coworking day pass", stage: "Most aware", clicks: 60, fit: 0.95 },
];

const SORTS = [
  { id: "traffic", label: "By traffic", key: (q) => q.clicks },
  { id: "fit", label: "By buyer fit", key: (q) => q.fit },
];

const STAGES = [
  { stage: "Unaware", query: "what is coworking", wants: "Find information", status: "covered", publish: "Explainer article" },
  { stage: "Problem-aware", query: "how to focus working from home", wants: "Solve a problem", status: "weak", publish: "How-to guide" },
  { stage: "Solution-aware", query: "best coworking space jakarta", wants: "Find a solution", status: "missing", publish: "Comparison page" },
  { stage: "Product-aware", query: "example space pricing", wants: "Learn about you", status: "missing", publish: "Pricing and locations page" },
  { stage: "Most aware", query: "book coworking day pass", wants: "Work with you", status: "covered", publish: "Booking page" },
];

const STATUS_LABEL = { covered: "Covered", weak: "Weak", missing: "Missing" };

const SPECS = [
  { n: "5", label: "Buying stages" },
  { n: "5", label: "Search intents" },
  { n: "4", label: "Job levels" },
  { n: "1", label: "Customer-fit score per query" },
];

const STUCK = [
  { title: "Random articles", consequence: "No topical authority" },
  { title: "Chasing volume", consequence: "Readers who never buy" },
  { title: "Overlapping posts", consequence: "Pages that compete with each other" },
];

const STEPS = [
  { title: "Connect Search Console", desc: "We read the queries Google already shows your site for." },
  { title: "Every query tagged", desc: "What the searcher wants, how close they are to buying, and whether they fit your customer." },
  { title: "Missing pages", desc: "See which stages and topics have no page, or only a weak one." },
  { title: "What to write next", desc: "A recommendation becomes an outline you edit." },
];

const SHIFT = [
  ["Topics picked by volume", "Topics picked by buying stage"],
  ["Isolated posts", "Connected articles that build topical authority"],
  ["Traffic totals", "Who reads, and how close they are to buying"],
];

const FOR_WHO = ["SEO agencies planning content for clients", "In-house content leads", "Marketing teams that report on leads"];

const NOT_THIS = [
  { name: "Keyword volume tool", icon: TrendingUp },
  { name: "Rank tracker", icon: Hash },
  { name: "AI article generator", icon: Sparkles },
];

const FAQS = [
  {
    q: "How is this different from keyword research tools?",
    a: "Keyword tools show search volume across the web. We start from the queries Google already shows your site for and sort them by who is searching and how close they are to buying.",
  },
  {
    q: "Do I need Google Search Console?",
    a: "Yes. You connect it with your Google account, and the analysis runs on your own site’s queries.",
  },
  {
    q: "What is a “buying stage”?",
    a: "How close a searcher is to choosing you. Marketers call these awareness stages: unaware, problem-aware, solution-aware, product-aware and most aware. Each stage needs a different page.",
  },
  {
    q: "Does it write the articles?",
    a: "It tells you what to write and who it’s for, then turns that recommendation into an outline you edit. The writing stays yours.",
  },
  {
    q: "Is there a free plan?",
    a: "Yes. The free plan covers one site and needs no credit card.",
  },
];

function Wordmark({ size = 24 }) {
  return (
    <Link to="/" className="wordmark" aria-label="WhyRank home">
      <HelpCircle size={size} aria-hidden="true" />
      <span>WHY<b>RANK</b></span>
    </Link>
  );
}

function Terms({ items }) {
  return (
    <ul className="terms">
      {items.map((t) => (
        <li key={t}><Check size={14} aria-hidden="true" />{t}</li>
      ))}
    </ul>
  );
}

function Status({ status }) {
  if (status === "covered") {
    return <span className="cell-present"><Check size={14} aria-hidden="true" />{STATUS_LABEL[status]}</span>;
  }
  return <span className="cell-missing">{STATUS_LABEL[status]}</span>;
}

// ---------------------------------------------------------------------------
// Navbar: a floating pill
// ---------------------------------------------------------------------------
function Navbar() {
  return (
    <header className="nav" data-testid="landing-navbar">
      <div className="container">
        <div className="nav__pill">
          <Wordmark />
          <nav aria-label="Page">
            <ul className="nav__links">
              <li><a href="#problem">Problem</a></li>
              <li><a href="#solution">How it works</a></li>
              <li><a href="#demo">Coverage map</a></li>
              <li><a href="#faq">FAQ</a></li>
            </ul>
          </nav>
          <Link to="/sign-in" className="btn btn--small" data-testid="navbar-cta">
            Sign in
          </Link>
        </div>
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Hero: Banner
// ---------------------------------------------------------------------------
function Hero() {
  const [sortId, setSortId] = useState("traffic");
  const sort = SORTS.find((x) => x.id === sortId);
  const rows = [...QUERIES].sort((x, y) => sort.key(y) - sort.key(x));
  const buyersInTop3 = rows.slice(0, 3).filter((q) => q.fit >= 0.5).length;

  const onTabKey = (e) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const next = sortId === "traffic" ? "fit" : "traffic";
    setSortId(next);
    document.getElementById(`sort-${next}`)?.focus();
  };

  return (
    <section className="hero" data-testid="landing-hero" aria-labelledby="hero-head">
      <div className="container hero__grid">
        <div className="hero__text">
          <h1 id="hero-head">Are you chasing traffic or qualified leads?</h1>
          <p className="lead">
            We sort your Search Console queries by buying stage and find missing pages.
          </p>
          <div className="hero__actions">
            <Link to="/sign-up" className="btn btn--primary" data-testid="hero-cta">
              Map my coverage
            </Link>
            <a href="#demo" className="btn">
              See an example map
            </a>
          </div>
          <Terms items={["Free plan, no credit card", "Connects to Search Console"]} />
        </div>

        <figure
          className="qpanel"
          data-slot="hero"
          data-alt="WhyRank Gap Analysis captured from the app: Search Console queries with awareness stage, clicks and buyer fit"
        >
          <div className="qpanel__head">
            <p className="qpanel__title">Search Console queries</p>
            <p className="mono qpanel__site">{SAMPLE_SITE}</p>
          </div>
          <div className="qpanel__tabs" role="tablist" aria-label="Sort queries">
            {SORTS.map((x) => (
              <button
                key={x.id}
                id={`sort-${x.id}`}
                type="button"
                role="tab"
                aria-selected={sortId === x.id}
                aria-controls="sort-panel"
                tabIndex={sortId === x.id ? 0 : -1}
                onClick={() => setSortId(x.id)}
                onKeyDown={onTabKey}
              >
                {x.label}
              </button>
            ))}
          </div>
          <div id="sort-panel" role="tabpanel" aria-labelledby={`sort-${sortId}`}>
            <table className="qtable">
              <caption className="visually-hidden">Sample queries sorted {sort.label.toLowerCase()}</caption>
              <thead>
                <tr>
                  <th scope="col">Query</th>
                  <th scope="col">Stage</th>
                  <th scope="col" className="num-col">Clicks</th>
                  <th scope="col">Buyer</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((q) => (
                  <tr key={q.query}>
                    <td className="mono">{q.query}</td>
                    <td data-label="Stage">{q.stage}</td>
                    <td data-label="Clicks" className="mono num-col">{q.clicks.toLocaleString("en-US")}</td>
                    <td data-label="Buyer">
                      {q.fit >= 0.5
                        ? <span className="cell-present"><Check size={14} aria-hidden="true" />Your buyer</span>
                        : <span className="cell-missing">Not your buyer</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="qpanel__foot" aria-live="polite">
            Top three {sort.label.toLowerCase()}: <b><span className="mono">{buyersInTop3}</span> of 3</b> are your buyers.
          </p>
        </figure>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Demo: the coverage map as one record
// ---------------------------------------------------------------------------
function DemoSection() {
  return (
    <section id="demo" className="demo" data-testid="demo-section" aria-labelledby="demo-head">
      <div className="container">
        <div className="head-split">
          <h2 id="demo-head">One topic, every buying stage</h2>
          <p className="lead">A sample map: which stages your pages cover and what to publish next.</p>
        </div>

        <figure
          className="report"
          data-slot="report"
          data-alt="A WhyRank coverage map captured from the app: queries by awareness stage, page coverage and the next recommendation"
        >
          <div className="report__head">
            <p className="report__title">Coverage map</p>
            <dl className="report__meta">
              <div>
                <dt>Topic</dt>
                <dd className="mono report__keyword">{SAMPLE_TOPIC}</dd>
              </div>
              <div>
                <dt>Site</dt>
                <dd className="mono report__keyword">{SAMPLE_SITE}</dd>
              </div>
            </dl>
          </div>

          <table className="report__table">
            <caption className="visually-hidden">Sample queries by buying stage, with page coverage and what to publish</caption>
            <thead>
              <tr>
                <th scope="col">Stage</th>
                <th scope="col">Example query</th>
                <th scope="col">Reader wants</th>
                <th scope="col">Your page</th>
                <th scope="col">Publish</th>
              </tr>
            </thead>
            <tbody>
              {STAGES.map((row) => (
                <tr key={row.stage} className={row.status === "missing" ? "is-gap" : undefined}>
                  <td>{row.stage}</td>
                  <td data-label="Example query" className="mono">{row.query}</td>
                  <td data-label="Reader wants">{row.wants}</td>
                  <td data-label="Your page"><Status status={row.status} /></td>
                  <td data-label="Publish">{row.publish}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="report__next">
            <div>
              <h3>Write next</h3>
              <p>&ldquo;Best coworking spaces in Jakarta, compared&rdquo; for solution-aware readers.</p>
            </div>
            <p className="stamp" aria-label="Your buyer">
              <b>Your buyer</b>
              <span>solution-aware</span>
            </p>
          </div>

          <dl className="report__stats">
            {SPECS.map((s, i) => (
              <div key={s.label} data-testid={`stat-${i}`}>
                <dt>{s.n}</dt>
                <dd>{s.label}</dd>
              </div>
            ))}
          </dl>
        </figure>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Problem
// ---------------------------------------------------------------------------
function ProblemSection() {
  return (
    <section id="problem" className="problem" data-testid="problem-section" aria-labelledby="problem-head">
      <div className="container">
        <div className="head-split">
          <h2 id="problem-head">Traffic that never becomes a lead</h2>
          <p className="lead">You write what gets searched most. Traffic grows. Leads don&rsquo;t.</p>
        </div>

        <div className="stuck">
          <h3>What chasing volume costs you</h3>
          <dl>
            {STUCK.map((item) => (
              <div key={item.title}>
                <dt>{item.title}</dt>
                <dd>{item.consequence}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Solution: how it works, then before against after
// ---------------------------------------------------------------------------
function SolutionSection() {
  return (
    <section id="solution" className="solution" data-testid="solution-section" aria-labelledby="solution-head">
      <div className="container">
        <h2 id="solution-head">From Search Console to a content plan</h2>
        <ol className="steps">
          {STEPS.map((step) => (
            <li key={step.title}>
              <h3>{step.title}</h3>
              <p>{step.desc}</p>
            </li>
          ))}
        </ol>

        <table className="shift">
          <caption>How the content plan changes</caption>
          <thead>
            <tr>
              <th scope="col">Before</th>
              <th scope="col">After</th>
            </tr>
          </thead>
          <tbody>
            {SHIFT.map(([before, after]) => (
              <tr key={before}>
                <td><span className="with-icon"><Minus size={18} aria-hidden="true" />{before}</span></td>
                <td><span className="with-icon"><Check size={18} aria-hidden="true" />{after}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Who it's for, and what it is not
// ---------------------------------------------------------------------------
function FeaturesSection() {
  return (
    <section className="forwho" data-testid="features-section" aria-labelledby="forwho-head">
      <div className="container">
        <div className="forwho__grid">
          <h2 id="forwho-head">Built for teams that need leads</h2>
          <ul>
            {FOR_WHO.map((item) => (
              <li key={item}><Check size={22} aria-hidden="true" />{item}</li>
            ))}
          </ul>
        </div>

        <div className="notthis">
          <div>
            <h3>Not another SEO tool</h3>
          </div>
          <div>
            <ul className="notthis__list">
              {NOT_THIS.map(({ name, icon: Icon }) => (
                <li key={name}><Icon size={16} aria-hidden="true" /><del>{name}</del></li>
              ))}
            </ul>
            <p className="decision">A content plan from your own search data</p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// FAQ
// ---------------------------------------------------------------------------
function FAQSection() {
  return (
    <section id="faq" className="faq" data-testid="faq-section" aria-labelledby="faq-head">
      <div className="container faq__grid">
        <h2 id="faq-head">Questions before you connect</h2>
        <div className="faq__list">
          {FAQS.map((faq) => (
            <div key={faq.q}>
              <h3>{faq.q}</h3>
              <p>{faq.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Final CTA on the ink band
// ---------------------------------------------------------------------------
function FinalCTA() {
  return (
    <section className="close" data-testid="final-cta" aria-labelledby="close-head">
      <div className="container close__grid">
        <h2 id="close-head">See which stages your content skips</h2>
        <div className="close__side">
          <div className="hero__actions">
            <Link to="/sign-up" className="btn btn--primary">
              Map my coverage
            </Link>
            <Link to="/sign-in" className="btn btn--on-ink">
              Sign in
            </Link>
          </div>
          <Terms items={["Free plan, no credit card", "Cancel anytime"]} />
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Footer: a statement
// ---------------------------------------------------------------------------
function Footer() {
  return (
    <footer className="footer" data-testid="landing-footer">
      <div className="container">
        <p className="footer__line">Content planning from Search Console, for SEO agencies and content teams.</p>
        <div className="footer__row">
          <Wordmark size={20} />
          <ul className="footer__links">
            <li><a href="#problem">Problem</a></li>
            <li><a href="#solution">How it works</a></li>
            <li><a href="#demo">Coverage map</a></li>
            <li><a href="#faq">FAQ</a></li>
            <li><Link to="/sign-in">Sign in</Link></li>
          </ul>
        </div>
      </div>
    </footer>
  );
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------
export default function Landing() {
  return (
    <div className="landing" data-testid="landing-page">
      <a className="skip" href="#main">Skip to content</a>
      <Navbar />
      <main id="main">
        <Hero />
        <DemoSection />
        <ProblemSection />
        <SolutionSection />
        <FeaturesSection />
        <FAQSection />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}
